import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, ref } from 'vue';
import * as Encoding from 'encoding-japanese';
import store from '@/store';
import router from '@/router';
import { commitAddNotification } from '@/store/main/mutations';
import {
  dispatchGetCompanyDepartments,
  dispatchGetCompanyUserCsv,
  dispatchPostCompanyUserCsv,
} from '@/store/main/actionsMain';
import {
  readAssertedCompanyId,
  readAssertedDepartments,
  readUserLocale,
} from '@/store/main/getters';
import CompanyUsersTable from '@/views/pages/companyUsers/CompanyUsersTable.vue';
import { usePermissionComposable } from '@/composables/usePermission';
import { useCompanyFeatureComposable } from '@/composables/useCompanyFeatures';
import { useUserComposable } from '@/composables/useUser';
import { useDepartmentComposable } from '@/composables/useDepartment';
import UserInviteDialog from '@/views/pages/companyUsers/UserInviteDialog.vue';
import DepartmentTreeSelect from '@/components/DepartmentTreeSelect.vue';
import {
  buildDepartmentOptions,
  DepartmentOption,
} from '@/utils/departmentTree';
import { defineMessages } from '@/lib/i18n';
import sharedMessages from '@/lib/shared-messages';


export default /*#__PURE__*/_defineComponent({
  __name: 'CompanyUsersPage',
  setup(__props) {

const m = defineMessages({
  addMember: 'ユーザーを招待する',
  inviteSameCompanyMembers: '同じテナントのメンバーを招待します',
  downloadAllUsersCsv:
    '同じテナントの全ユーザーをCSVファイルでダウンロードします',
  csvFileError: 'CSVファイルを選択してください。',
});

const { users, getCompanyUsers } = useUserComposable();
const { hasPermission, PERMISSIONS: P } = usePermissionComposable();
const { isScimEnabled } = useCompanyFeatureComposable();

const isUserInviteDialogVisible = ref(false);
const isFilterPanelVisible = ref(false);
const filterCriteria = ref({
  name: '',
  email: '',
  departments: [] as DepartmentOption[],
});

// 一時的なフィルター設定オブジェクト
const filterCriteriaTemp = ref({
  name: '',
  email: '',
  departments: [] as DepartmentOption[],
});

const inputCsvFile = ref<HTMLInputElement | null>(null);
const selectableDepartments = computed(() => readAssertedDepartments(store));
const { departmentTree, getCompanyDepartmentTree } = useDepartmentComposable();

// Computed property for API locale format
const apiLocale = computed(() => {
  return readUserLocale(store) === 'ja-JP' ? 'ja' : 'en';
});

const removeNameFilter = () => {
  filterCriteria.value.name = '';
  filterCriteriaTemp.value.name = '';
  applyFilters();
};

const removeEmailFilter = () => {
  filterCriteria.value.email = '';
  filterCriteriaTemp.value.email = '';
  applyFilters();
};

const removeDepartmentFilter = (uuid: string) => {
  filterCriteria.value.departments = filterCriteria.value.departments.filter(
    (department) => department.uuid !== uuid
  );
  filterCriteriaTemp.value.departments =
    filterCriteriaTemp.value.departments.filter(
      (department) => department.uuid !== uuid
    );
  applyFilters();
};

const updateFilterDepartments = (uuids: string[]) => {
  filterCriteriaTemp.value.departments = uuids
    .map((uuid) => departmentOptions.value.find((d) => d.uuid === uuid))
    .filter((d): d is DepartmentOption => d !== undefined);
};

const fetchUsers = async () => {
  await getCompanyUsers({ fetchAuthInfo: true, includeSwitchable: true });
};

const toggleFilterPanel = () => {
  isFilterPanelVisible.value = !isFilterPanelVisible.value;
};

const cancelFilters = () => {
  isFilterPanelVisible.value = false;
};

const applyFilters = () => {
  filterCriteria.value = { ...filterCriteriaTemp.value };
  fetchUsers();
};

const resetFilters = () => {
  filterCriteria.value = {
    name: '',
    email: '',
    departments: [],
  };
  filterCriteriaTemp.value = { ...filterCriteria.value };
  applyFilters();
};

/** 階層順に罫線情報を付けた部署の選択肢。 */
const departmentOptions = computed(() =>
  buildDepartmentOptions(selectableDepartments.value)
);

const filteredUsers = computed(() => {
  return users.value.filter((user) => {
    const matchesName = filterCriteria.value.name
      ? `${user.last_name}${user.middle_name}${user.first_name}`.includes(
          filterCriteria.value.name
        )
      : true;
    const matchesEmail = filterCriteria.value.email
      ? user.email.includes(filterCriteria.value.email)
      : true;
    const matchesDepartment = filterCriteria.value.departments.length
      ? filterCriteria.value.departments.some((selectedDept) =>
          user?.departments?.some((dept) => dept?.uuid === selectedDept.uuid)
        )
      : true;
    return matchesName && matchesEmail && matchesDepartment;
  });
});

const hasFilterCriteria = computed(() => {
  return (
    filterCriteria.value.name ||
    filterCriteria.value.email ||
    filterCriteria.value.departments.length > 0
  );
});

const handleCsvDownload = async () => {
  const res = await dispatchGetCompanyUserCsv(store, {
    companyId: readAssertedCompanyId(store),
    locale: apiLocale.value,
  });

  if (res) {
    const unicodeArray = Encoding.stringToCode(res.data);
    const sjisArray = Encoding.convert(unicodeArray, 'SJIS', 'UNICODE');
    const blob = new Blob([new Uint8Array(sjisArray)], {
      type: 'text/csv;charset=shift-jis;',
    });

    // Create download link
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'users.csv';

    link.click();
    URL.revokeObjectURL(link.href);
  }
};

const handleUploadButtonClick = () => {
  const input = inputCsvFile.value;

  if (input) {
    input.click();
  }
};

const handleChangeInput = async (e: Event) => {
  try {
    const target = e.target as HTMLInputElement;
    const file = target.files?.[0];

    // Validate file selection
    if (!file) {
      commitAddNotification(store, {
        messageKey: sharedMessages.csvFileRequired,
        color: 'error',
      });
      return;
    }

    // Validate file type
    if (file.type !== 'text/csv') {
      return;
    }

    // Get current locale for upload
    const companyId = readAssertedCompanyId(store);

    await dispatchPostCompanyUserCsv(store, {
      companyId,
      file,
      locale: apiLocale.value,
    });

    // Refresh users list after successful upload
    await fetchUsers();
  } catch (e) {
    console.error(e);
  }
};

const handleClickInput = async (e: Event) => {
  const target = e.target as HTMLInputElement;
  // 同じ名前のファイルを選択してもchangeイベントが発火するようにする
  target.value = '';
};

/**
 * 部署一覧の「ユーザー数」からの遷移を受ける。
 *
 * ツリー側の件数は配下を含む集計なので、クエリには対象部署 + 全子孫の uuid が
 * カンマ区切りで入ってくる。部署の読み込み後でないと名前を解決できない。
 */
const applyDepartmentFilterFromQuery = () => {
  const raw = router.currentRoute.query.department_uuids;
  const uuids = (Array.isArray(raw) ? raw.join(',') : raw ?? '')
    .split(',')
    .filter(Boolean);
  if (!uuids.length) {
    return;
  }

  const departments = departmentOptions.value.filter((department) =>
    uuids.includes(department.uuid)
  );
  if (!departments.length) {
    return;
  }

  filterCriteria.value = { ...filterCriteria.value, departments };
  filterCriteriaTemp.value = { ...filterCriteria.value };
};

onMounted(async () => {
  await fetchUsers();
  await dispatchGetCompanyDepartments(store, {
    companyId: readAssertedCompanyId(store),
  });
  await getCompanyDepartmentTree();
  applyDepartmentFilterFromQuery();
});

return { __sfc: true,m, users, getCompanyUsers, hasPermission, P, isScimEnabled, isUserInviteDialogVisible, isFilterPanelVisible, filterCriteria, filterCriteriaTemp, inputCsvFile, selectableDepartments, departmentTree, getCompanyDepartmentTree, apiLocale, removeNameFilter, removeEmailFilter, removeDepartmentFilter, updateFilterDepartments, fetchUsers, toggleFilterPanel, cancelFilters, applyFilters, resetFilters, departmentOptions, filteredUsers, hasFilterCriteria, handleCsvDownload, handleUploadButtonClick, handleChangeInput, handleClickInput, applyDepartmentFilterFromQuery, CompanyUsersTable, UserInviteDialog, DepartmentTreeSelect, sharedMessages }
}

})